(() => {
  if (customElements.get('premium-video-carousel')) return;

  class PremiumVideoCarousel extends HTMLElement {
    constructor() {
      super();
      this.viewport = this.querySelector('.pvc-carousel__viewport');
      this.track = this.querySelector('.pvc-carousel__track');
      this.slides = Array.from(this.querySelectorAll('.pvc-slide'));
      this.prevButton = this.querySelector('.pvc-button--prev');
      this.nextButton = this.querySelector('.pvc-button--next');
      this.dots = Array.from(this.querySelectorAll('.pvc-dot'));

      this.loop = this.dataset.loop === 'true';
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.currentIndex = 0;
      this.activeVideo = null;
      this.sectionVisible = true;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.scrollTimer = null;
      this.resizeTimer = null;
    }

    connectedCallback() {
      if (!this.viewport || !this.slides.length) return;

      this.bindControls();
      this.bindEvents();
      this.setupIntersectionObserver();

      requestAnimationFrame(() => {
        this.updateActiveByPosition();
        this.goToSlide(this.currentIndex, true);
      });
    }

    disconnectedCallback() {
      if (this.sectionObserver) this.sectionObserver.disconnect();
    }

    bindControls() {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.goToRelative(-1));
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.goToRelative(1));
      }

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = Number(dot.dataset.dotIndex);
          this.goToSlide(index);
        });
      });

      this.slides.forEach((slide, index) => {
        const soundButton = slide.querySelector('.pvc-slide__sound-toggle');
        const video = slide.querySelector('.pvc-slide__video');

        if (soundButton && video) {
          soundButton.addEventListener('click', async (event) => {
            event.stopPropagation();

            const isActive = index === this.currentIndex;

            if (!isActive) {
              this.goToSlide(index);
            }

            this.pauseAllVideos();
            this.loadVideoSource(video);

            video.muted = !video.muted;

            if (video.muted) {
              video.removeAttribute('data-user-unmuted');
            } else {
              video.setAttribute('data-user-unmuted', 'true');
            }

            soundButton.setAttribute('aria-pressed', String(!video.muted));

            const playVideo = async () => {
              try {
                await video.play();
                slide.classList.add('pvc-is-playing');
                this.activeVideo = video;
              } catch (error) {
                slide.classList.remove('pvc-is-playing');
              }
            };

            if (video.readyState >= 2) {
              await playVideo();
            } else {
              video.addEventListener(
                'canplay',
                async () => {
                  await playVideo();
                },
                { once: true }
              );
            }
          });
        }

        slide.addEventListener('click', (event) => {
          if (event.target.closest('a, button')) return;
          if (index !== this.currentIndex) {
            this.goToSlide(index);
          }
        });
      });
    }

    bindEvents() {
      this.viewport.addEventListener('scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => {
          this.updateActiveByPosition();
        }, 80);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goToRelative(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goToRelative(1);
        }
      });

      this.setAttribute('tabindex', '0');
      this.setAttribute('role', 'region');
      this.setAttribute('aria-roledescription', 'carousel');

      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          this.goToSlide(this.currentIndex, true);
        }, 120);
      });
    }

    setupIntersectionObserver() {
      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.sectionVisible = entry.isIntersecting;
            if (!entry.isIntersecting) {
              this.pauseAllVideos();
            } else {
              this.playActiveVideo();
            }
          });
        },
        { threshold: 0.35 }
      );

      this.sectionObserver.observe(this);
    }

    goToRelative(direction) {
      let nextIndex = this.currentIndex + direction;

      if (this.loop) {
        if (nextIndex < 0) nextIndex = this.slides.length - 1;
        if (nextIndex >= this.slides.length) nextIndex = 0;
      } else {
        nextIndex = Math.max(0, Math.min(this.slides.length - 1, nextIndex));
      }

      this.goToSlide(nextIndex);
    }

    goToSlide(index, instant = false) {
      const slide = this.slides[index];
      if (!slide) return;

      this.currentIndex = index;

      const viewportRect = this.viewport.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const currentScroll = this.viewport.scrollLeft;
      const targetScroll =
        currentScroll + (slideRect.left - viewportRect.left) - (viewportRect.width / 2 - slideRect.width / 2);

      this.viewport.scrollTo({
        left: targetScroll,
        behavior: instant || this.reducedMotion.matches ? 'auto' : 'smooth'
      });

      this.setActiveState(index);
    }

    updateActiveByPosition() {
      const viewportRect = this.viewport.getBoundingClientRect();
      const viewportCenter = viewportRect.left + viewportRect.width / 2;

      let closestIndex = 0;
      let closestDistance = Infinity;

      this.slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const slideCenter = rect.left + rect.width / 2;
        const distance = Math.abs(viewportCenter - slideCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== this.currentIndex) {
        this.currentIndex = closestIndex;
      }

      this.setActiveState(this.currentIndex);
    }

    setActiveState(index) {
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('pvc-is-active', i === index);
      });

      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-selected', String(i === index));
      });

      if (this.prevButton) {
        this.prevButton.disabled = !this.loop && index === 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = !this.loop && index === this.slides.length - 1;
      }

      this.playActiveVideo();
    }

    playActiveVideo() {
      this.pauseAllVideos();

      if (!this.autoplayEnabled || this.reducedMotion.matches || !this.sectionVisible) return;

      const activeSlide = this.slides[this.currentIndex];
      if (!activeSlide) return;

      const video = activeSlide.querySelector('.pvc-slide__video');
      if (!video) return;

      if (!video.hasAttribute('data-user-unmuted')) {
        video.muted = true;
      }
      this.loadVideoSource(video);
      this.activeVideo = video;

      const attemptPlay = () => {
        const playPromise = video.play();

        if (playPromise && typeof playPromise.then === 'function') {
          playPromise
            .then(() => {
              activeSlide.classList.add('pvc-is-playing');
            })
            .catch(() => {
              activeSlide.classList.remove('pvc-is-playing');
            });
        } else {
          activeSlide.classList.add('pvc-is-playing');
        }
      };

      if (video.readyState >= 2) {
        attemptPlay();
      } else {
        video.addEventListener('loadeddata', attemptPlay, { once: true });
        video.addEventListener('canplay', attemptPlay, { once: true });
      }
    }

    pauseAllVideos() {
      this.slides.forEach((slide) => {
        const video = slide.querySelector('.pvc-slide__video');
        if (!video) return;

        video.pause();
        try {
          video.currentTime = 0;
        } catch (e) {}

        slide.classList.remove('pvc-is-playing');
      });

      this.activeVideo = null;
    }

    loadVideoSource(video) {
      if (video.dataset.loaded === 'true') return;

      const sources = video.querySelectorAll('source[data-src]');
      sources.forEach((source) => {
        if (!source.getAttribute('src')) {
          source.setAttribute('src', source.dataset.src);
        }
      });

      video.load();
      video.dataset.loaded = 'true';
    }
  }

  customElements.define('premium-video-carousel', PremiumVideoCarousel);
})();